<template>
  <!-- The game chat (SPEC §5.6): on a phone a window rising from the bottom of the screen, on a
       desktop a panel beside the board (or in the replay's side panel). -->
  <transition :name="layout === 'sheet' ? 'chat-slide' : ''">
    <div v-if="chat.shown" class="game-chat" :class="layout" :style="frameStyle" role="dialog" :aria-label="t('chat-title')">
      <div class="chat-head">
        <span class="chat-title">{{ t('chat-title') }}</span>
        <button type="button" class="chat-close" :aria-label="t('chat-close')" :title="t('chat-close')" @click="chat.close()">×</button>
      </div>
      <div ref="list" class="chat-list" @scroll="onScroll">
        <div v-if="chat.loading" class="chat-note">{{ t('page-loading') }}</div>
        <button v-else-if="chat.loadFailed" type="button" class="chat-note chat-retry" @click="chat.load(false)">{{ t('chat-load-failed') }}</button>
        <div v-else-if="!rows.length" class="chat-note chat-empty">{{ t('chat-empty') }}</div>
        <template v-for="r in rows">
          <div v-if="r.divider" :key="'d' + r.key" ref="divider" class="chat-divider"><span>{{ t('chat-new') }}</span></div>
          <div :key="r.key" class="chat-message" :class="{ mine: r.mine, pending: r.pending }">
            <div v-if="r.meta" class="chat-meta">
              <span v-if="!r.mine">{{ r.name }}</span><span v-if="r.spectator">{{ t('chat-spectator') }}</span><template v-if="!r.mine"> · </template>
              <button v-if="canJump" type="button" class="chat-move" @click="$emit('jump', r.moveCount)">{{ t('chat-move', { n: r.moveCount }) }}</button>
              <span v-else class="chat-move">{{ t('chat-move', { n: r.moveCount }) }}</span>
              · {{ r.time }}
            </div>
            <div class="chat-text">{{ r.text }}</div>
            <button v-if="r.failed" type="button" class="chat-failed" :title="r.failed" @click="chat.send(null, r.p)">{{ t('chat-send-failed') }}</button>
          </div>
        </template>
      </div>
      <form class="chat-input" @submit.prevent="send">
        <input ref="input" v-model="draft" type="text" maxlength="500" :placeholder="t('chat-placeholder')" enterkeyhint="send" />
        <button type="submit" class="btn-main chat-send" :disabled="!draft.trim()">{{ t('button-chat-send') }}</button>
      </form>
    </div>
  </transition>
</template>

<script>
import { t } from '@/texts';

function pad(n) { return String(n).padStart(2, '0'); }

// A message's time: the time alone today, 昨天 and the time yesterday, else the date and time.
function timeText(iso) {
  const d = new Date(iso), now = new Date();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const day = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return time;
  if (days === 1) return t('chat-time-yesterday', { time });
  return t('chat-time-date', { month: d.getMonth() + 1, day: d.getDate(), time });
}

export default {
  name: 'GameChat',

  props: {
    chat: { type: Object, required: true },
    // 'sheet' (phone) or 'panel' (desktop).
    layout: { type: String, required: true },
    // Where the frame sits: the sheet's top, or the panel's box (page coordinates).
    frame: { type: Object, default: () => ({}) },
    // The ids of the players (the others are spectators).
    playerIds: { type: Array, default: () => [] },
    // In a finished game's replay a message's move label moves the board there.
    canJump: Boolean,
  },

  data() {
    return { draft: '', atBottom: true };
  },

  computed: {
    frameStyle() {
      const px = v => (v === undefined ? undefined : `${v}px`);
      if (this.layout === 'sheet') return { top: px(this.frame.top) };
      return { left: px(this.frame.left), top: px(this.frame.top), width: px(this.frame.width), height: px(this.frame.height) };
    },

    rows() {
      const rows = this.chat.messages.map(m => ({
        key: `n${m.n}`,
        mine: m.user.id === this.chat.me,
        name: m.user.displayName,
        spectator: !this.playerIds.includes(m.user.id),
        moveCount: m.moveCount,
        time: timeText(m.time),
        text: m.text,
        meta: true,
        divider: this.chat.dividerAfter !== null && m.n === this.firstNew,
      }));
      for (const p of this.chat.pending) {
        if (p.n !== null && this.chat.messages.some(m => m.n === p.n)) continue;
        rows.push({ key: `p${p.local}`, mine: true, pending: !p.failed, text: p.text, failed: p.failed, p, meta: false });
      }
      return rows;
    },

    firstNew() {
      const m = this.chat.messages.find(x => x.n > this.chat.dividerAfter && x.user.id !== this.chat.me);
      return m ? m.n : null;
    },
  },

  watch: {
    'chat.shown'(shown) {
      if (shown) this.$nextTick(() => this.scrollOnOpen());
    },
    rows(now, before) {
      if (now.length === before.length) return;
      const follow = this.atBottom;
      this.$nextTick(() => { if (follow) this.scrollToBottom(); });
    },
  },

  mounted() {
    if (this.chat.shown) this.$nextTick(() => this.scrollOnOpen());
  },

  methods: {
    t,

    scrollOnOpen() {
      const list = this.$refs.list;
      if (!list) return;
      const divider = this.$refs.divider && this.$refs.divider[0];
      if (divider) list.scrollTop = Math.max(0, divider.offsetTop - list.offsetTop - 8);
      else this.scrollToBottom();
      this.onScroll();
    },

    scrollToBottom() {
      const list = this.$refs.list;
      if (list) list.scrollTop = list.scrollHeight;
      this.atBottom = true;
    },

    onScroll() {
      const list = this.$refs.list;
      if (list) this.atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 40;
    },

    send() {
      const text = this.draft.trim();
      if (!text) return;
      this.draft = '';
      this.atBottom = true;
      this.chat.send(text);
    },
  },
};
</script>

<style scoped>
.game-chat {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #fff;
  overflow: hidden;
  text-align: left;
  z-index: 50;
}

/* Phone: rises from the bottom of the screen, 16 px in from each side (SPEC §5.6). */
.game-chat.sheet {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 0;
  border-radius: 14px 14px 0 0;
  box-shadow: 0 -3px 16px rgba(0, 0, 0, .25);
}

.game-chat.panel {
  position: absolute;
  border: 1px solid #c8b28a;
  border-radius: 10px;
}

.chat-slide-enter-active { transition: transform 150ms ease-out; }
.chat-slide-leave-active { transition: transform 120ms ease-in; }
.chat-slide-enter, .chat-slide-leave-to { transform: translateY(100%); }
@media (prefers-reduced-motion: reduce) {
  .chat-slide-enter-active, .chat-slide-leave-active { transition: none; }
}

.chat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  padding: 4px 6px 4px 12px;
  border-bottom: 1px solid #eee;
}

.chat-title { font-size: 16px; color: #2c3e50; }

.chat-close {
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: none;
  color: #888;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.chat-list {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 12px 8px;
  -webkit-overflow-scrolling: touch;
}

/* A short conversation sits at the bottom, by the input, as in messaging apps. */
.chat-list > :first-child { margin-top: auto; }

.chat-note {
  display: block;
  margin: 24px auto;
  border: none;
  background: none;
  color: #999;
  font-size: 14px;
  text-align: center;
}

.chat-retry { color: #d00; cursor: pointer; }

.chat-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 6px 0;
}

.chat-message.mine { align-items: flex-end; }

.chat-meta {
  margin: 0 4px 2px;
  font-size: 12px;
  color: #999;
}

.chat-move {
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
}

button.chat-move { text-decoration: underline; cursor: pointer; }

.chat-text {
  max-width: 78%;
  padding: 7px 11px;
  border-radius: 12px;
  border-top-left-radius: 3px;
  background: #f2f2f2;
  color: #2c3e50;
  font-size: 15px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.mine .chat-text {
  border-top-left-radius: 12px;
  border-top-right-radius: 3px;
  background: #f3e6d3;
}

.pending .chat-text { opacity: .55; }

.chat-failed {
  margin: 2px 4px 0;
  padding: 0;
  border: none;
  background: none;
  color: #d00;
  font-size: 12px;
  cursor: pointer;
}

.chat-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
  color: #0175ff;
  font-size: 12px;
}

.chat-divider::before, .chat-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #bcd7ff;
}

.chat-input {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  padding: 8px 10px;
  border-top: 1px solid #eee;
}

.chat-input input {
  flex: 1;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
}

.chat-send {
  flex: none;
  height: 38px;
  margin: 0;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 15px;
}
</style>
